import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NotificationService } from '@app/shared/notification/notification.service';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { FlatpickrDefaults } from 'angularx-flatpickr';
import { Observable } from 'rxjs';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';
import { PreventNumberScrollDirective } from '@app/shared/directives/prevent-number-scroll.directive';

@Component({
  selector: 'app-daily-pricing-bulk-update',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, DatePickerComponent, PreventNumberScrollDirective],
  providers: [FlatpickrDefaults],
  template: `
    <div class="bg-white dark:bg-white/[0.03] rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-800">
      <h3 class="text-lg font-semibold mb-4 text-gray-800 dark:text-white/90">Bulk Update Daily Prices</h3>

      <form [formGroup]="bulkUpdateForm" (ngSubmit)="onSubmit()">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <app-date-picker
              [label]="'Start Date'"
              [name]="'start_date'"
              [control]="startDateControl">
            </app-date-picker>
          </div>

          <div>
            <app-date-picker
              [label]="'End Date'"
              [name]="'end_date'"
              [control]="endDateControl"
              [minDate]="startDateControl.value">
            </app-date-picker>
          </div>

          <div>
            <label for="price" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Price (£)
            </label>
            <input
              type="number"
              id="price"
              step="0.01"
              formControlName="price"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
              [class.border-red-500]="bulkUpdateForm.get('price')?.invalid && bulkUpdateForm.get('price')?.touched"
            />
          </div>
        </div>

        <div class="flex justify-end gap-3">
          <app-button
            type="button"
            [label]="isDeleting ? 'Removing...' : 'Clear Date Range'"
            [style]="'danger'"
            [disabled]="startDateControl.invalid || endDateControl.invalid || isDeleting || isSubmitting"
            (click)="onRemove()">
          </app-button>
          <app-button
            type="submit"
            [label]="isSubmitting ? 'Updating...' : 'Update Date Range'"
            [style]="'primary'"
            [disabled]="bulkUpdateForm.invalid || isSubmitting || isDeleting">
          </app-button>
        </div>
      </form>
    </div>
  `
})
export class DailyPricingBulkUpdateComponent implements OnInit {
  @Input() storeBulkDailyPrices!: (data: any) => Observable<any>;
  @Input() deleteBulkDailyPrices!: (data: any) => Observable<any>;
  @Output() onSuccess = new EventEmitter<void>();

  bulkUpdateForm!: FormGroup;
  isSubmitting = false;
  isDeleting = false;

  startDateControl!: FormControl;
  endDateControl!: FormControl;
  priceControl!: FormControl;

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private confirmationModal: ConfirmationModalService,
  ) {}

  ngOnInit(): void {
    this.startDateControl = new FormControl('', [Validators.required]);
    this.endDateControl = new FormControl('', [Validators.required]);
    // TVG-307: no lower bound — a credit product's date ranges hold negative figures,
    // matching what TVG-66 allowed on a straight product price.
    this.priceControl = new FormControl('', [Validators.required]);

    this.bulkUpdateForm = this.fb.group({
      start_date: this.startDateControl,
      end_date: this.endDateControl,
      price: this.priceControl,
    });
  }

  onSubmit(): void {
    if (this.bulkUpdateForm.invalid) {
      this.notificationService.error('Please fill in all required fields');
      return;
    }

    this.isSubmitting = true;
    const formData = this.bulkUpdateForm.value;

    this.storeBulkDailyPrices(formData).subscribe({
      next: (response) => {
        this.notificationService.success(`Bulk prices updated successfully (${response.count} dates)`);
        this.bulkUpdateForm.reset();
        this.isSubmitting = false;
        this.onSuccess.emit();
      },
      error: (error) => {
        this.notificationService.error('Failed to update bulk prices');
        this.isSubmitting = false;
      },
    });
  }

  async onRemove(): Promise<void> {
    if (this.startDateControl.invalid || this.endDateControl.invalid) {
      this.notificationService.error('Please select start and end dates');
      return;
    }

    const confirmed = await this.confirmationModal.confirm({
      title: 'Clear date range',
      message: 'Are you sure you want to remove prices for this date range?',
      confirmLabel: 'Remove Prices',
      cancelLabel: 'Keep Prices',
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.isDeleting = true;
    const formData = {
      start_date: this.startDateControl.value,
      end_date: this.endDateControl.value,
    };

    this.deleteBulkDailyPrices(formData).subscribe({
      next: (response) => {
        this.notificationService.success(`Prices removed successfully (${response.count} dates)`);
        this.bulkUpdateForm.reset();
        this.isDeleting = false;
        this.onSuccess.emit();
      },
      error: (error) => {
        this.notificationService.error('Failed to remove prices');
        this.isDeleting = false;
      },
    });
  }

  populateForm(startDate: Date, endDate: Date, price: number | null): void {
    // Format dates to Y-m-d format expected by date picker
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    this.startDateControl.setValue(formatDate(startDate));
    this.endDateControl.setValue(formatDate(endDate));
    // Set price to null if empty date was clicked, otherwise set the price
    this.priceControl.setValue(price !== null ? price : '');
  }
}
